import { useState, useEffect } from 'react';
import { PlusIcon, DocumentTextIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useDropzone } from 'react-dropzone';
import { getShowPreviewPdfUrl, MAX_PREVIEW_PDF_BYTES, MAX_PREVIEW_PDF_LABEL } from '../../../util/show-images';
import Modal from './Modal';
import ModalActions from './ModalActions';

// strips the "<uuid>_" prefix added on upload
export function pdfDisplayName(path) {
  return path.split('/').pop().replace(/^[0-9a-f-]{36}_/i, '');
}

/** Picks the preview PDF; the file is uploaded when the parent show form is saved. */
function ManagePreviewPdfModal({ isOpen, setIsOpen, pdfFile, pdfPath, onPdfSaved }) {
  const [file, setFile] = useState(null);
  const [path, setPath] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setFile(pdfFile);
      setPath(pdfPath);
      setError(null);
    }
  }, [isOpen, pdfFile, pdfPath]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    maxSize: MAX_PREVIEW_PDF_BYTES,
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        const { file: rejected, errors } = fileRejections[0];
        const tooLarge = errors.some(e => e.code === 'file-too-large');
        setError(
          tooLarge
            ? `"${rejected.name}" is too large (over ${MAX_PREVIEW_PDF_LABEL}) and was not added.`
            : `"${rejected.name}" is not a PDF and was not added.`
        );
        return;
      }
      setError(null);
      setFile(acceptedFiles[0]);
    },
  });

  const name = file?.name ?? (path && pdfDisplayName(path));
  // an unsaved file has no stored copy to open yet
  const href = !file && path ? getShowPreviewPdfUrl(path) : null;
  const hasChanges = file !== pdfFile || path !== pdfPath;

  const handleRemove = () => {
    setFile(null);
    setPath(null);
  };

  const handleSave = () => {
    onPdfSaved({ file, path });
    setIsOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title="Manage PDF"
      // description={
      //   <p className="text-xs text-slate-400 mt-1">
      //     Upload a PDF preview of the show. It replaces the preview URL once the show is saved.
      //   </p>
      // }
      maxWidth="max-w-md"
      zIndex="z-[60]"
    >
      {error && (
        <div className="mb-4 rounded-lg bg-secondary-red/10 border border-secondary-red/30 px-3 py-2 text-sm text-secondary-red">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-4">
        {name ? (
          <div className="relative h-40 w-40 rounded-lg border border-slate-200 flex flex-col items-center justify-center px-3">
            {href ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 min-w-0 w-full hover:opacity-80 transition"
              >
                <DocumentTextIcon className="h-10 w-10 text-secondary-red" />
                <span className="text-xs text-slate-600 text-center break-all line-clamp-3">{name}</span>
              </a>
            ) : (
              <div className="flex flex-col items-center gap-2 min-w-0 w-full">
                <DocumentTextIcon className="h-10 w-10 text-slate-300" />
                <span className="text-xs text-slate-600 text-center break-all line-clamp-3">{name}</span>
              </div>
            )}
            <button
              type="button"
              onClick={handleRemove}
              aria-label="Remove PDF"
              className="absolute top-1 right-1 rounded-full bg-black/40 hover:bg-black/60 text-white p-1 transition focus:outline-none"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div
            {...getRootProps()}
            className="h-40 w-40 rounded-lg border border-dashed border-black/20 hover:border-solid flex flex-col items-center justify-center cursor-pointer transition-colors"
          >
            <input {...getInputProps()} />
            <PlusIcon className="h-6 w-6 mb-1 text-slate-300" />
            <span className="text-xs text-center leading-tight px-2 text-slate-400">Click or drag</span>
            <span className="text-[10px] text-center leading-tight px-2 text-slate-400 mt-0.5">
              One PDF, max {MAX_PREVIEW_PDF_LABEL}
            </span>
          </div>
        )}
      </div>

      <ModalActions onCancel={() => setIsOpen(false)} onConfirm={handleSave} confirmDisabled={!hasChanges} />
    </Modal>
  );
}

export default ManagePreviewPdfModal;
